<template>
	<section :class="`${wrapperCls} ${variantCls.calendar}`">
		<section :class="variantCls.navigation">
			<div
				class="xs ml1 mr1 fs4 fw7 p2 cu2"
				@click="decrementMonth(viewingMonth)"
			>
				❮
			</div>
			<select
				id="months"
				v-model="viewingMonth.month"
				name="months"
				:class="variantCls.select"
			>
				<option
					v-for="month of months"
					:key="month.label"
					:value="month.value"
					>{{ month.label }}</option
				>
			</select>
			<select
				id="years"
				v-model="viewingMonth.year"
				name="years"
				:class="variantCls.select"
			>
				<option v-for="year of years" :key="year">{{ year }}</option>
			</select>
			<div
				class="xs ml1 mr1 fs4 fw7 ta3 p2 cu2"
				@click="incrementMonth(viewingMonth)"
			>
				❯
			</div>
		</section>
		<section id="weekdays" class="flex xs-between bb1 pt2 pb2">
			<span
				v-for="weekday of weekdays"
				:key="weekday"
				:class="variantCls.weekdays"
				>{{ weekday }}</span
			>
		</section>
		<section id="dates" class="flex wrap xs-between">
			<button
				v-for="date of monthView"
				:key="date.value"
				:class="date.class"
				:disabled="date.disabled"
				style="flex-basis: calc(100% / 7);"
				@click="selectDate(date)"
			>
				{{ date.date }}
			</button>
		</section>
	</section>
</template>

<script>
import dayjs from "dayjs"
const yyyymmddRegex = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/
const isDateString = currentValue =>
	typeof currentValue === "string" && yyyymmddRegex.test(currentValue)
const isDate = currentValue =>
	currentValue instanceof Date && typeof currentValue.getFullYear === "function"
function pad(n) {
	return n.toString().length === 1 ? `0${n.toString()}` : n.toString()
}
function serializeDate(val) {
	if (!isDate(val)) {
		if (isDateString(val)) return val
		return
	}
	return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())}`
}
export default {
	name: "JCalendar",
	props: {
		variant: {
			type: String,
			default: "default"
		},
		displayMode: {
			type: String,
			default: "inline"
		},
		value: {
			type: [Date, String, Array, Object],
			validator(val) {
				return (
					isDateString(val) ||
					isDate(val) ||
					(Array.isArray(val) && (val.every(isDateString) || val.every(isDate)))
				)
			},
			default() {
				return new Date()
			}
		},
		mode: {
			type: String,
			validator(value) {
				return ["single", "multiple", "range"].includes(value)
			},
			default() {
				return "single"
			}
		},
		minDate: {
			type: [Date, String, Array, Object],
			required: false
		},
		maxDate: {
			type: [Date, String, Array, Object],
			required: false
		},
		years: {
			type: Array,
			required: false,
			default: () => [...Array(130)].map((_, i) => new Date().getFullYear() - i)
		}
	},
	data() {
		return {
			viewingMonth: {
				year: new Date().getFullYear(),
				month: new Date().getMonth()
			},
			selectedDates: [],
			locale: "en-US"
		}
	},
	computed: {
		wrapperCls() {
			const classMapper = {
				popover: "xs-absolute z-10 bg4 mt10",
				inline: ""
			}
			return classMapper[this.displayMode]
		},
		variantCls() {
			// return this.$store.getters.variants("JCalendar", this.variant)
			return {
				// calendar: "w-64 px-2 border-c8 border rounded mt-2",
				calendar: "xs-12 pl2 pr2 b2 bc5 mt2 br2",
				// navigation: "py-4 flex justify-center items-center",
				navigation: "pl4 pr4 flex xs-center xs-middle",
				// select:
				// 	"flex-1 mx-1 bg-c4 p-1 rounded leading-normal text-sm font-bold",
				select: "flex ml1 mr1 p1 fw7 fs4 r1 bc4",
				// date: "py-2 border-b border-c8 text-c7 hover:bg-c9",
				date: "pt2 pb2 c6 bc7 bg7 fs4",
				// currentMonthDate:
				// 	"font-semibold py-2 border-b border-c8 text-c6 hover:bg-c9",
				currentMonthDate: "fs4 fw6 pt2 pb2 b1 bc7 bg8",
				selectedDate: "pt2 pb2 bg2 c4 fw7",
				disabledDate: "pointer-none pt2 pb2 c8",
				weekdays: "xs fs4 fw7 xs-between ta2"
				// "flex-1 text-xs text-gray-700 font-bold justify-between text-center"
			}
		},
		serializedValue() {
			return (Array.isArray(this.value) ? this.value : [this.value]).map(
				serializeDate
			)
		},
		valueType() {
			return this.mode !== "single"
				? "array"
				: isDate(this.value)
				? "date"
				: "string"
		},
		months() {
			return [...Array(12)]
				.map((_, i) =>
					Intl.DateTimeFormat(this.locale, { month: "long" }).format(
						new Date(new Date().getFullYear(), i)
					)
				)
				.map((label, value) => ({ label, value }))
		},
		weekdays() {
			const offset = new Date().getDay()
			return [...Array(7)].map((_, i) =>
				Intl.DateTimeFormat(this.locale, { weekday: "short" }).format(
					new Date(
						new Date().getFullYear(),
						new Date().getMonth(),
						new Date().getDate() - offset + i
					)
				)
			)
		},
		monthView() {
			const offset =
				new Date(this.viewingMonth.year, this.viewingMonth.month, 1).getDay() -
				1
			return [...Array(42)]
				.map(
					(_, i) =>
						new Date(
							this.viewingMonth.year,
							this.viewingMonth.month,
							i - offset
						)
				)
				.map(date => {
					const d = date.getDate()
					const m = date.getMonth()
					const y = date.getFullYear()
					function pad(n) {
						return n.toString().length === 1 ? `0${n.toString()}` : n.toString()
					}
					const serialized = `${y}-${pad(m + 1)}-${pad(d)}`
					return {
						date: d,
						month: m,
						year: y,
						serialized: serializeDate(date),
						disabled:
							new Date(y, m, d) < this.minDate ||
							new Date(y, m, d) > this.maxDate,
						class: this.serializedValue.includes(serialized)
							? this.variantCls.selectedDate
							: new Date(y, m, d) < this.minDate ||
							  new Date(y, m, d) > this.maxDate
							? this.variantCls.disabledDate
							: m === this.viewingMonth.month
							? this.variantCls.currentMonthDate
							: this.variantCls.date,
						current: Boolean(date.getMonth() === this.viewingMonth.month),
						ariaLabel: `${Intl.DateTimeFormat(this.locale, {
							day: "numeric"
						}).format(date)}, ${Intl.DateTimeFormat(this.locale, {
							year: "numeric",
							weekday: "long",
							month: "long"
						}).format(date)}`
					}
					// aria-label="12, Wednesday December 2018, Dee Dee's birthday"
				})
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				if (
					val &&
					val instanceof Date &&
					typeof val.getFullYear === "function"
				) {
					this.viewingMonth = {
						year: val.getFullYear(),
						month: val.getMonth()
					}
				} else if (val && typeof val === "string" && yyyymmddRegex.test(val)) {
					this.viewingMonth = {
						year: parseInt(val.substr(0, 4)),
						month: parseInt(val.substr(5, 2)) - 1
					}
				}
			}
		}
	},
	methods: {
		decrementMonth(viewingMonth) {
			if (viewingMonth.month > 0) {
				viewingMonth.month -= 1
			} else if (this.years.includes(viewingMonth.year - 1)) {
				viewingMonth.month = 11
				viewingMonth.year -= 1
			}
		},
		incrementMonth(viewingMonth) {
			if (viewingMonth.month < 11) {
				viewingMonth.month += 1
			} else if (this.years.includes(viewingMonth.year + 1)) {
				viewingMonth.month = 0
				viewingMonth.year += 1
			}
		},
		selectDate({ date, month, year, serialized }) {
			switch (this.mode) {
				case "single":
					this.$emit(
						"input",
						this.valueType === "string"
							? serialized
							: dayjs(new Date(year, month, date)).format("YYYY-MM-DD")
					)
					this.$emit("blur")
					break
				case "multiple":
					this.multipleSelect({ date, month, year })
					break
				case "range":
					this.rangeSelect({ date, month, year, serialized })
					break
			}
		},
		rangeSelect({ date, month, year, serialized }) {
			const typedInputValue =
				this.value[0] && isDate(this.value[0])
					? new Date(year, month, date)
					: serialized
			const newVal =
				this.value.length === 1
					? [...this.value, typedInputValue].sort()
					: [typedInputValue]
			this.$emit("input", newVal)
		},
		multipleSelect({ date, month, year, serialized }) {
			const typedInputValue =
				this.value[0] && isDate(this.value[0])
					? new Date(year, month, date)
					: serialized
			const newVal = this.value.includes(typedInputValue)
				? this.value.filter(x => x !== typedInputValue)
				: [...this.value, typedInputValue]
			this.$emit("input", newVal)
		}
	}
}
</script>

<style></style>
